import { useState } from "react";
import { Sparkles, Plus, X, Loader2, Calendar } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

// FIN-6 — SugestoesModal.
// Lista sugestões do detect com confidence visual + ações por linha
// (Adicionar / Ignorar). User confirma — backend NUNCA cria automático.

const fmtBRL = (v) => Number(v ?? 0).toLocaleString("pt-BR", {
  style: "currency", currency: "BRL",
});

const FREQ_LABEL = {
  monthly: "Mensal",
  annual: "Anual",
  weekly: "Semanal",
  custom: "Custom",
};

const CONFIDENCE_META = {
  high:   { label: "Alta",   variant: "success" },
  medium: { label: "Média",  variant: "warning" },
  low:    { label: "Baixa",  variant: "outline" },
};

export default function SugestoesModal({
  open,
  onOpenChange,
  suggestions,
  isLoading,
  onAdd,            // (suggestion) => Promise
}) {
  // ignored = sugestões dispensadas localmente (não persistido)
  const [ignored, setIgnored] = useState(new Set());
  const [addingMerchant, setAddingMerchant] = useState(null);

  const visible = (suggestions || []).filter(s => !ignored.has(s.merchant));

  const handleAdd = async (s) => {
    if (addingMerchant) return;
    setAddingMerchant(s.merchant);
    try {
      await onAdd(s);
      toast.success(`${s.suggested_name} adicionada`);
    } catch (err) {
      toast.error(err.message || "Falha ao adicionar");
    } finally {
      setAddingMerchant(null);
    }
  };

  const handleIgnore = (s) => {
    setIgnored(prev => {
      const n = new Set(prev);
      n.add(s.merchant);
      return n;
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-surface-overlay border border-subtle max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="sub-suggestions-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" data-testid="sub-suggestions-title">
            <Sparkles className="w-5 h-5 text-status-warning" aria-hidden="true" />
            Detectamos {visible.length} {visible.length === 1 ? "possível assinatura" : "possíveis assinaturas"}
          </DialogTitle>
          <DialogDescription className="text-foreground-muted text-caption">
            Baseado nas suas transações dos últimos 4 meses. Confirme as que fizerem sentido — o Nexo não cria nada sem sua confirmação.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="py-12 flex items-center justify-center gap-2 text-foreground-muted">
            <Loader2 className="w-4 h-4 animate-spin" /> Buscando padrões…
          </div>
        ) : visible.length === 0 ? (
          <div className="py-12 text-center text-foreground-muted">
            <Sparkles className="w-8 h-8 opacity-40 mx-auto mb-3" />
            <p className="text-caption">Nenhum padrão recorrente detectado nos últimos 4 meses.</p>
            <p className="text-micro text-foreground-subtle mt-1">
              Quando houver pagamentos repetidos no mesmo merchant, eles aparecem aqui.
            </p>
          </div>
        ) : (
          <div className="space-y-2 py-2">
            {visible.map(s => {
              const cm = CONFIDENCE_META[s.confidence] || CONFIDENCE_META.low;
              const isAdding = addingMerchant === s.merchant;
              return (
                <div
                  key={s.merchant}
                  className={cn(
                    "rounded-lg border border-subtle bg-surface-card-hover/40 p-4 flex items-start gap-3",
                    isAdding && "opacity-60",
                  )}
                  data-testid={`sub-suggestion-${s.merchant.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <p className="text-foreground font-semibold truncate">{s.suggested_name}</p>
                      <Badge variant={cm.variant} className="text-micro">{cm.label} confiança</Badge>
                    </div>
                    <p className="text-micro text-foreground-muted mb-2">
                      {s.merchant} · {FREQ_LABEL[s.frequency] || s.frequency} · dia {s.billing_day}
                      {s.category_guess ? ` · ${s.category_guess}` : ""}
                    </p>
                    <div className="flex items-center gap-3 text-caption flex-wrap">
                      <span className="font-mono font-bold text-foreground tabular-nums">
                        {fmtBRL(s.amount)}
                      </span>
                      <span className="text-foreground-muted">
                        {s.occurrences} ocorrência{s.occurrences === 1 ? "" : "s"}
                      </span>
                      <span className="text-foreground-muted inline-flex items-center gap-1">
                        <Calendar className="w-3 h-3" aria-hidden="true" />
                        {s.last_seen}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 shrink-0">
                    <Button
                      type="button"
                      size="sm"
                      variant="primary"
                      onClick={() => handleAdd(s)}
                      disabled={isAdding}
                      className="bg-brand-green/15 border border-brand-green/40 text-brand-green hover:bg-brand-green/25"
                      data-testid={`sub-suggestion-${s.merchant.toLowerCase().replace(/\s/g, "-")}-add`}
                    >
                      {isAdding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                      Adicionar
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => handleIgnore(s)}
                      data-testid={`sub-suggestion-${s.merchant.toLowerCase().replace(/\s/g, "-")}-ignore`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="sub-suggestions-close">
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
